import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HEADER } from '../constants/testIds'

const SQUARE_LOGO = 'https://customer-assets-jai6qajn.emergentagent.net/job_be6b6d40-04a2-4626-96b0-39ae7bcf5dea/artifacts/wk1w9zwb_logo.jpg'

const navLinks = [
  { label: 'Sobre mí', href: '#sobre-mi', testId: HEADER.navSobreMi },
  { label: 'Proyectos', href: '#proyectos', testId: HEADER.navProyectos },
  { label: 'Auditoría', href: '#auditoria', testId: 'nav-auditoria' },
  { label: 'Contacto', href: '#contacto', testId: HEADER.navContacto },
]

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleNav = (e, href) => {
    e.preventDefault()
    setMenuOpen(false)
    setTimeout(() => {
      const el = document.querySelector(href)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }, menuOpen ? 300 : 0)
  }

  return (
    <>
      <motion.header
        data-testid={HEADER.container}
        className={`fixed top-0 left-0 right-0 z-50 px-5 md:px-10 py-4 flex items-center justify-between transition-all duration-700 ${
          scrolled ? 'bg-black/95 backdrop-blur-md' : ''
        }`}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* ─── Logo ─── */}
        <a
          href="#"
          data-testid={HEADER.logo}
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
          className="flex items-center gap-3 shrink-0"
        >
          {/* JO. lettermark */}
          <img
            src={SQUARE_LOGO}
            alt="JO."
            style={{ height: '36px', width: '36px', objectFit: 'contain', borderRadius: 0 }}
          />

          {/* "José Osuna." wordmark */}
          <div className="flex items-center gap-3">
            <span
              className="text-white font-bold leading-none hidden sm:block"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '16px', letterSpacing: '-0.01em' }}
            >
              José Osuna
              <span style={{ color: '#F5A623' }}>.</span>
            </span>

            {/* Divider */}
            <div
              className="hidden md:block w-px bg-white/20"
              style={{ height: '18px' }}
            />

            {/* DISEÑADOR WEB */}
            <span
              className="hidden md:block text-white/40 uppercase tracking-widest"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', letterSpacing: '0.2em' }}
            >
              Diseñador Web
            </span>
          </div>
        </a>

        {/* ─── Desktop Nav ─── */}
        <nav data-testid={HEADER.nav} className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={(e) => handleNav(e, link.href)}
              className="text-xs tracking-[0.15em] text-white/50 hover:text-white transition-colors duration-300 uppercase"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contacto"
            data-testid={HEADER.navHablemos}
            onClick={(e) => handleNav(e, '#contacto')}
            className="text-xs tracking-[0.15em] text-amber-400 hover:text-white transition-colors duration-300 uppercase"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Hablemos ↗
          </a>
        </nav>

        {/* ─── Mobile ─── */}
        <div className="flex md:hidden items-center gap-5">
          <a
            href="#contacto"
            onClick={(e) => handleNav(e, '#contacto')}
            className="text-xs tracking-[0.15em] text-amber-400 uppercase"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Hablemos ↗
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-[5px] p-1 bg-transparent border-0 cursor-pointer"
          >
            <motion.span className="block w-5 h-px bg-white origin-center"
              animate={menuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3 }} />
            <motion.span className="block w-5 h-px bg-white"
              animate={menuOpen ? { opacity: 0 } : { opacity: 1 }} transition={{ duration: 0.2 }} />
            <motion.span className="block w-5 h-px bg-white origin-center"
              animate={menuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3 }} />
          </button>
        </div>
      </motion.header>

      {/* ─── Mobile menu ─── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu-panel"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-black flex flex-col justify-center items-start px-8"
          >
            <div className="flex flex-col gap-8">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5 }}
                  className="text-4xl font-black uppercase text-white hover:text-amber-400 transition-colors duration-300"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '-0.01em' }}
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="#contacto"
                onClick={(e) => handleNav(e, '#contacto')}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.38, duration: 0.5 }}
                className="text-4xl font-black uppercase text-amber-400"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '-0.01em' }}
              >
                Hablemos ↗
              </motion.a>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-10 left-8 text-white/20 text-xs tracking-[0.25em] uppercase"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              José Osuna · Córdoba, España
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
